import { useCallback, useState } from 'react';
import { Tooltip } from 'antd';
import * as Yup from 'yup';
import { useInviteUserMutation } from '@/redux';
import { Role, YupValue } from '@/core';
import { Form, notify } from '@/components';
import { PasswordTooltip } from './PasswordTooltip';
import classes from './UserAddPanel.module.scss';

const schema = Yup.object().shape({
  email: Yup.string().email().field().nullable().required(),
  firstName: Yup.string().field().nullable().required(),
  lastName: Yup.string().field().nullable().required(),
  password: Yup.string().password().nullable().required(),
  passwordConfirmation: Yup.string()
    .oneOf([Yup.ref('password'), null], { key: 'signup.passwordConfirmationMatchError' })
    .nullable()
    .required(),
  role: Yup.string().nullable().required(),
});

type FormValue = YupValue<typeof schema>;

const useSubmit = () => {
  const [invite] = useInviteUserMutation();

  return useCallback(
    async (value: FormValue) =>
      invite(value)
        .unwrap()
        .then(() => notify.success.t('users.invited')),
    [invite],
  );
};

const INITIAL_VALUE: FormValue = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  passwordConfirmation: '',
  role: Role.USER,
};

export function UserAddPanel() {
  const handleSubmit = useSubmit();
  const [isPasswordTooltip, setIsPasswordTooltip] = useState(false);

  const toggleTooltip = () => setIsPasswordTooltip((prev) => !prev);

  return (
    <Form.Formik<FormValue>
      uid="user-add"
      i18nKeyPrefix="users.add"
      initialValues={INITIAL_VALUE}
      validationSchema={schema}
      onSubmit={handleSubmit}
    >
      <div className={classes.userAddPanel}>
        <Form.Input name="email" required />
        <Form.Input name="firstName" required />
        <Form.Input name="lastName" required />
        <Tooltip
          overlayStyle={{ maxWidth: '350px' }}
          title={<PasswordTooltip />}
          visible={isPasswordTooltip}
          placement="right"
        >
          <Form.Input
            onFocus={toggleTooltip}
            onBlur={toggleTooltip}
            name="password"
            type="password"
            required
          />
        </Tooltip>
        <Form.Input name="passwordConfirmation" type="password" required />
        <Form.EnumSelect name="role" type={Role} required />
      </div>
    </Form.Formik>
  );
}
